/**
 * PartyAddressFields
 *
 * Multi-address management for parties/entities.
 * Each address carries a "Use as" tag array (billing, shipping, consignee, …).
 * No separate billing/shipping sections — one list, one add-form.
 */

import { useState, useEffect, useCallback } from 'react';

import { EMPTY_ADDRESS, EMPTY_PENDING_ADDRESS } from './party-dialog-helpers';
import { getStates } from '../../../services/geolocation-service';
import { FormField, Input, Select, Button, Checkbox, CountrySelect, StateSelect, QuickPasteAddress, useToast } from '../../ui';

import type { PartyFormData, PendingAddress } from './party-dialog-helpers';
import type { EntityAddress } from '../../../services/entity-service';
import type { State } from '../../../services/geolocation-service';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const USAGE_OPTIONS = [
  { value: 'billing', label: 'Billing' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'consignee', label: 'Consignee' },
];

const ADDRESS_TYPES = [
  { value: 'office', label: 'Office' },
  { value: 'residential', label: 'Residential' },
  { value: 'warehouse', label: 'Warehouse' },
];

// PendingAddress is imported from party-dialog-helpers (shared with flush-on-save logic)

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

interface PartyAddressFieldsProps {
  formData: PartyFormData;
  setFormData: React.Dispatch<React.SetStateAction<PartyFormData>>;
}

// eslint-disable-next-line max-lines-per-function -- aggregator component; sub-components (AddAddressForm, AddressRow) + handlers (handleAdd, handleSaveEdit, handleEdit) already extracted
export function PartyAddressFields({ formData, setFormData }: PartyAddressFieldsProps): JSX.Element {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO(refactor): verify dependency array intentional
  const addresses = formData.addresses ?? [];
  const { addToast } = useToast();

  // Pending add-form state lives in formData so handleSave can flush it
  const newAddr: PendingAddress = formData.pendingAddress ?? EMPTY_PENDING_ADDRESS;
  const isFormOpen = formData.pendingAddressOpen ?? false;

  const setNewAddr = useCallback((updater: PendingAddress | ((prev: PendingAddress) => PendingAddress)): void => {
    setFormData(prev => {
      const next = typeof updater === 'function' ? updater(prev.pendingAddress ?? EMPTY_PENDING_ADDRESS) : updater;
      return { ...prev, pendingAddress: next };
    });
  }, [setFormData]);

  const setIsFormOpen = useCallback((open: boolean): void => {
    setFormData(prev => ({ ...prev, pendingAddressOpen: open }));
  }, [setFormData]);

  // Editing existing addresses still uses local state (not flushed on save)
  const [states, setStates] = useState<State[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editAddr, setEditAddr] = useState<PendingAddress>(EMPTY_PENDING_ADDRESS);

  useEffect(() => {
    const countryCode = editingIdx !== null ? editAddr.countryCode : newAddr.countryCode;
    if (!countryCode) { return; }
    setIsLoadingStates(true);
    void getStates(countryCode)
      .then(setStates)
      .catch(() => setStates([]))
      .finally(() => setIsLoadingStates(false));
  }, [newAddr.countryCode, editAddr.countryCode, editingIdx]);

  const toggleUsage = useCallback((use: string): void => {
    setNewAddr(prev => ({
      ...prev,
      usage: prev.usage.includes(use)
        ? prev.usage.filter(u => u !== use)
        : [...prev.usage, use],
    }));
  }, [setNewAddr]);

  const toggleEditUsage = useCallback((use: string): void => {
    setEditAddr(prev => ({
      ...prev,
      usage: prev.usage.includes(use)
        ? prev.usage.filter(u => u !== use)
        : [...prev.usage, use],
    }));
  }, []);

  const handleRemove = useCallback((idx: number): void => {
    if (editingIdx === idx) { setEditingIdx(null); setEditAddr(EMPTY_PENDING_ADDRESS); }
    setFormData(prev => {
      const updated = (prev.addresses ?? []).filter((_, i) => i !== idx);
      return applyBillingSync({ ...prev, addresses: updated });
    });
  }, [setFormData, editingIdx]);

  const handleEdit = useCallback((idx: number): void => {
    const addr = addresses[idx];
    if (!addr) {return}
    setEditingIdx(idx);
    setIsFormOpen(false);
    setEditAddr(entityToPendingAddress(addr));
  }, [addresses, setIsFormOpen]);

  const handleAdd = useCallback((): void => {
    const err = validatePendingAddress(newAddr);
    if (err) { addToast({ variant: 'error', message: err }); return; }
    const addr = pendingToEntityAddress(newAddr, addresses.length === 0);
    setFormData(prev => applyBillingSync({
      ...prev,
      addresses: [...prev.addresses, addr],
      pendingAddress: EMPTY_PENDING_ADDRESS,
      pendingAddressOpen: false,
    }));
  }, [newAddr, addresses.length, setFormData, addToast]);

  const handleSaveEdit = useCallback((): void => {
    const err = validatePendingAddress(editAddr);
    if (err) { addToast({ variant: 'error', message: err }); return; }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- editingIdx is non-null in this code path
    const isPrimaryAddress = addresses[editingIdx!]?.isPrimary ?? false;
    const addr = pendingToEntityAddress(editAddr, isPrimaryAddress);
    setFormData(prev => applyBillingSync({
      ...prev,
      addresses: prev.addresses.map((a, i) =>
        i === editingIdx ? { ...addr, id: a.id, isPrimary: a.isPrimary } : a),
    }));
    setEditingIdx(null);
    setEditAddr(EMPTY_PENDING_ADDRESS);
  }, [editAddr, editingIdx, addresses, setFormData, addToast]);

  const handleCancelForm = useCallback((): void => {
    setIsFormOpen(false);
    setFormData(prev => ({ ...prev, pendingAddress: EMPTY_PENDING_ADDRESS }));
  }, [setIsFormOpen, setFormData]);

  const handleCancelEdit = useCallback((): void => {
    setEditingIdx(null);
    setEditAddr(EMPTY_PENDING_ADDRESS);
  }, []);

  const isEditingExisting = editingIdx !== null;

  return (
    <div>
      <h3 className="text-base font-semibold text-foreground el-section-heading el-mb-sm border-b border-border">
        Addresses
      </h3>

      <div className="el-form-layout el-mt-sm">
        {addresses.length > 0 && (
          <div className="el-space-y-sm el-mb-sm el-bleed">
            {addresses.map((addr, idx) => (
              <AddressRow
                key={addr.id ?? idx}
                address={addr}
                isEditing={editingIdx === idx}
                onEdit={() => handleEdit(idx)}
                onRemove={() => handleRemove(idx)}
              />
            ))}
          </div>
        )}

        {isEditingExisting && (
          <div className="w-full">
            <AddAddressForm
              addr={editAddr}
              isLoadingStates={isLoadingStates}
              saveLabel="Update Address"
              states={states}
              onAdd={handleSaveEdit}
              onCancel={handleCancelEdit}
              onChange={setEditAddr}
              onToggleUsage={toggleEditUsage}
            />
          </div>
        )}

        {!isFormOpen && !isEditingExisting && (
          <div className="el-bleed">
            <Button size="sm" type="button" variant="secondary" onClick={() => setIsFormOpen(true)}>
              + Add Address
            </Button>
          </div>
        )}

        {isFormOpen && !isEditingExisting && (
          <div className="w-full">
            <AddAddressForm
              addr={newAddr}
              isLoadingStates={isLoadingStates}
              states={states}
              onAdd={handleAdd}
              onCancel={handleCancelForm}
              onChange={setNewAddr}
              onToggleUsage={toggleUsage}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Validation / conversion helpers
// ---------------------------------------------------------------------------

function validatePendingAddress(addr: PendingAddress): string | null {
  if (!addr.line1.trim()) {return 'Address line 1 is required'}
  if (!addr.city.trim()) {return 'City is required'}
  if (!addr.postalCode.trim()) {return 'Postal code is required'}
  return null;
}

function resolveUsage(addr: EntityAddress): string[] {
  if (addr.usage) {return addr.usage}
  return addr.label ? [addr.label] : [];
}

function entityToPendingLocale(addr: EntityAddress):
{ country: string; countryCode: string; state: string; stateCode: string; gstin: string } {
  return {
    country: addr.country ?? 'India',
    countryCode: addr.countryCode ?? 'IN',
    state: addr.state ?? '',
    stateCode: addr.stateCode ?? '',
    gstin: addr.gstin ?? '',
  };
}

function entityToPendingAddress(addr: EntityAddress): PendingAddress {
  return {
    type: addr.type ?? 'office',
    line1: addr.line1 ?? '',
    line2: addr.line2 ?? '',
    city: addr.city ?? '',
    postalCode: addr.postalCode ?? '',
    ...entityToPendingLocale(addr),
    usage: resolveUsage(addr),
  };
}

function pendingToEntityAddress(addr: PendingAddress, isPrimary: boolean): EntityAddress {
  return {
    label: addr.usage[0] ?? 'other',
    type: addr.type,
    usage: addr.usage.length > 0 ? addr.usage : undefined,
    line1: addr.line1.trim(),
    line2: addr.line2.trim() || undefined,
    city: addr.city.trim(),
    state: addr.state || undefined,
    stateCode: addr.stateCode || undefined,
    postalCode: addr.postalCode.trim(),
    country: addr.country,
    countryCode: addr.countryCode,
    gstin: addr.gstin.trim() || undefined,
    isPrimary,
  };
}

// ---------------------------------------------------------------------------
// Sync helper — keeps formData.billingAddress in step with the address list
// so geolocation effects (business identifiers, state labels) keep working.
// ---------------------------------------------------------------------------

function applyBillingSync(prev: PartyFormData): PartyFormData {
  const billing = prev.addresses.find(a => (a.usage?.includes('billing') ?? false) || a.label === 'billing');
  if (!billing) {
    return {
      ...prev,
      billingAddress: { ...EMPTY_ADDRESS, countryCode: prev.billingAddress.countryCode ?? 'IN' },
    };
  }
  return {
    ...prev,
    billingAddress: {
      line1: billing.line1,
      line2: billing.line2,
      city: billing.city,
      state: billing.state,
      stateCode: billing.stateCode,
      postalCode: billing.postalCode,
      country: billing.country,
      countryCode: billing.countryCode ?? 'IN',
    },
    billingAddressType: billing.type === 'residential' || billing.type === 'warehouse'
      ? billing.type
      : 'office',
  };
}

// ---------------------------------------------------------------------------
// AddressRow
// ---------------------------------------------------------------------------

function AddressRow({ address, isEditing, onEdit, onRemove }: { address: EntityAddress; isEditing: boolean; onEdit: () => void; onRemove: () => void }): JSX.Element {
  let tags: string[] = [];
  if (address.usage?.length) {
    tags = address.usage;
  } else if (address.label) {
    tags = [address.label];
  }

  return (
    <div className={`border rounded-md el-p-inner overflow-hidden ${isEditing ? 'border-primary bg-primary/5' : 'border-border'}`}>
      <div className="flex items-start justify-between el-gap-sm">
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center flex-wrap el-gap-sm mb-1">
            {tags.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary capitalize"
              >
                {tag}
              </span>
            ))}
            <span className="text-xs text-foreground-secondary capitalize">{address.type}</span>
          </div>
          <p className="text-sm truncate">{address.line1}{address.line2 ? `, ${address.line2}` : ''}</p>
          <p className="text-xs text-foreground-secondary truncate">
            {[address.city, address.state, address.postalCode, address.country].filter(Boolean).join(', ')}
          </p>
          {address.gstin && (
            <p className="text-xs text-foreground-secondary mt-0.5 truncate">GSTIN: {address.gstin}</p>
          )}
        </div>
        <div className="flex items-center el-gap-xs shrink-0">
          {!isEditing && (
            <Button size="sm" type="button" variant="ghost" onClick={onEdit}>Edit</Button>
          )}
          <Button size="sm" type="button" variant="ghost" onClick={onRemove}>Remove</Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddAddressForm
// ---------------------------------------------------------------------------

interface AddAddressFormProps {
  addr: PendingAddress;
  states: State[];
  isLoadingStates: boolean;
  saveLabel?: string;
  onChange: (updater: PendingAddress | ((prev: PendingAddress) => PendingAddress)) => void;
  onToggleUsage: (use: string) => void;
  onAdd: () => void;
  onCancel: () => void;
}

type AddrSetter = (updater: PendingAddress | ((prev: PendingAddress) => PendingAddress)) => void;

function handleQuickPaste(onChange: AddrSetter): (parsed: Partial<PendingAddress>) => void {
  return (parsed) => {
    // `||` is intentional: empty strings from parser should be treated as "no value".
    /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
    onChange(prev => ({
      ...prev,
      line1: parsed.line1 || prev.line1,
      line2: parsed.line2 || prev.line2,
      city: parsed.city || prev.city,
      state: parsed.state || prev.state,
      stateCode: parsed.stateCode || prev.stateCode,
      postalCode: parsed.postalCode || prev.postalCode,
      ...(parsed.gstin ? { gstin: parsed.gstin } : {}),
    }))
    /* eslint-enable @typescript-eslint/prefer-nullish-coalescing */
  };
}

function AddressLineFields({ addr, onChange }: { addr: PendingAddress; onChange: AddrSetter }): JSX.Element {
  const set = <K extends keyof PendingAddress>(key: K, value: PendingAddress[K]): void => {
    onChange(prev => ({ ...prev, [key]: value }));
  };
  return (
    <>
      <FormField compact label="Address Type">
        <Select options={ADDRESS_TYPES} value={addr.type} onChange={val => set('type', val)} />
      </FormField>
      <FormField compact label="Country">
        <CountrySelect
          placeholder="Select country"
          value={addr.countryCode}
          onChange={(code, name) => onChange(prev => ({
            ...prev, countryCode: code ?? 'IN', country: name ?? 'India', state: '', stateCode: '',
          }))}
        />
      </FormField>
      <FormField compact required className="col-span-2" label="Address Line 1">
        <Input placeholder="Street address" value={addr.line1} onChange={e => set('line1', e.target.value)} />
      </FormField>
      <FormField compact className="col-span-2" label="Address Line 2">
        <Input placeholder="Apartment, suite, unit, etc." value={addr.line2} onChange={e => set('line2', e.target.value)} />
      </FormField>
      <FormField compact required label="City">
        <Input placeholder="City" value={addr.city} onChange={e => set('city', e.target.value)} />
      </FormField>
      <FormField compact required label="Postal Code">
        <Input placeholder="Postal code" value={addr.postalCode} onChange={e => set('postalCode', e.target.value)} />
      </FormField>
    </>
  );
}

function AddressRegionFields({
  addr, states, isLoadingStates, onChange,
}: { addr: PendingAddress; states: State[]; isLoadingStates: boolean; onChange: AddrSetter }): JSX.Element {
  const set = <K extends keyof PendingAddress>(key: K, value: PendingAddress[K]): void => {
    onChange(prev => ({ ...prev, [key]: value }));
  };
  return (
    <>
      {(states.length > 0 || isLoadingStates) && (
        <FormField compact label="State / Province">
          <StateSelect
            countryCode={addr.countryCode}
            placeholder="Select state"
            value={addr.stateCode}
            onChange={(stateCode, stateName) => onChange(prev => ({
              ...prev, stateCode: stateCode ?? '', state: stateName ?? '',
            }))}
          />
        </FormField>
      )}
      {addr.countryCode === 'IN' && (
        <FormField compact label="GSTIN">
          <Input
            maxLength={15}
            placeholder="22AAAAA0000A1Z5"
            value={addr.gstin}
            onChange={e => set('gstin', e.target.value.toUpperCase())}
          />
        </FormField>
      )}
    </>
  );
}

function UsageCheckboxes({
  addr, onToggleUsage,
}: { addr: PendingAddress; onToggleUsage: (use: string) => void }): JSX.Element {
  return (
    <div className="col-span-2">
      <p className="text-xs font-medium text-foreground-secondary">Use as</p>
      <div className="flex items-center el-gap-md">
        {USAGE_OPTIONS.map(opt => (
          <Checkbox
            key={opt.value}
            hideMessage
            checked={addr.usage.includes(opt.value)}
            label={opt.label}
            size="sm"
            onChange={() => onToggleUsage(opt.value)}
          />
        ))}
      </div>
    </div>
  );
}

function AddAddressForm({ addr, states, isLoadingStates, saveLabel = 'Add Address', onChange, onToggleUsage, onAdd, onCancel }: AddAddressFormProps): JSX.Element {
  return (
    <div className="el-space-y-sm el-mt-sm">
      <QuickPasteAddress
        id="party-address-paste"
        includeGSTIN={addr.countryCode === 'IN'}
        onParse={handleQuickPaste(onChange)}
      />
      <div className="grid grid-cols-2 el-gap-field">
        <AddressLineFields addr={addr} onChange={onChange} />
        <AddressRegionFields addr={addr} isLoadingStates={isLoadingStates} states={states} onChange={onChange} />
        <UsageCheckboxes addr={addr} onToggleUsage={onToggleUsage} />
      </div>
      <div className="el-bleed flex justify-end el-gap-sm">
        <Button size="sm" type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button size="sm" type="button" variant="secondary" onClick={onAdd}>{saveLabel}</Button>
      </div>
    </div>
  );
}
